import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogIn } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import PersonalAreaButton from './PersonalAreaButton';

const APP_URL = import.meta.env.VITE_APP_URL || '';

const NAV_ITEMS = [
  { key: 'home', to: '/' },
  { key: 'parents', to: '/parents' },
  { key: 'kindergartens', to: '/kindergartens' },
  { key: 'networks', to: '/networks' },
  { key: 'emergency', to: '/emergency' },
  { key: 'contact', to: '/contact' },
];

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 start-0 end-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] border-b border-gray-100/80'
          : 'bg-white/60 backdrop-blur-md'
      }`}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-cyan-600 focus:text-white focus:rounded-lg focus:outline-none"
      >
        {t('accessibility.skipToContent')}
      </a>

      <nav aria-label={t('accessibility.title')} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <img src="/kipikid-logo.png" alt="KipiKid" className="w-10 h-10" />
            <span className="text-xl font-bold text-gray-900 tracking-tight">KipiKid</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-cyan-700 bg-cyan-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {t(`nav.${item.key}`)}
                </Link>
              );
            })}
          </div>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <PersonalAreaButton />
          </div>

          {/* Mobile: personal area + menu button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => APP_URL ? (window.location.href = `${APP_URL}/login`) : navigate('/login')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-cyan-600 text-white text-xs font-semibold hover:bg-cyan-700 transition-colors shadow-sm"
              aria-label={t('nav.personalArea')}
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>{t('nav.personalArea')}</span>
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2.5 rounded-xl hover:bg-gray-100/80 text-gray-700 transition-colors"
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-lg overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {NAV_ITEMS.map(item => {
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isActive ? 'text-cyan-700 bg-cyan-50' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                );
              })}
              <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                <LanguageSwitcher />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
